import { defineStore } from 'pinia';
import uniqBy from 'lodash-es/uniqBy';
import { wrapPiniaStore } from '@devhacker/shared/utils/wrapPiniaStore';
import { getAccessToken, getSubId } from '@devhacker/shared/features/auth/utils/token';
import { useBadgeStore } from '~/features/badge/store';

import type { Notification } from './schema';
import { fetchNotifications as fetchNotificationsApi, resetNotifications } from './api';
import { allowedNotifications } from './config';

export const useNotificationStore = wrapPiniaStore(
  defineStore('notification', () => {
    const { showBadge } = useBadgeStore();

    const notifications = ref<Notification[]>([]);
    const notificationsRequested = ref(false);
    const unviewedNotificationsCount = ref(0);

    function emptyNotifications() {
      notifications.value = [];
    }

    function addNotification(payload: Notification) {
      if (allowedNotifications.includes(payload.event)) {
        notifications.value = uniqBy([payload, ...notifications.value], 'package_uuid');
      }
    }

    function updateNotifications(payload: Notification[]) {
      const allowedPayload = payload.filter((n) => allowedNotifications.includes(n.event));
      notifications.value = [...notifications.value, ...allowedPayload];
    }

    function setNotificationsRequested() {
      notificationsRequested.value = true;
    }

    function setReadToNotification(notificationId: string) {
      notifications.value = notifications.value.map((item) => {
        const is_read = item._id === notificationId ? 1 : item.is_read;
        return { ...item, is_read };
      });
    }

    async function fetchNotifications() {
      const token = getAccessToken();
      if (!token) return;

      const subId = getSubId(token);
      if (!subId) return;

      try {
        const { data } = await fetchNotificationsApi(subId, {
          page: 1,
          limit: 10,
        });

        updateNotifications(data);
      } catch (err) {
        showErrorBadge(err);
      }
    }

    async function fetchUnviewedNotificationsCount() {
      const token = getAccessToken();
      if (!token) return;

      const subId = getSubId(token);
      if (!subId) return;

      try {
        const { meta } = await fetchNotificationsApi(subId, {
          page: 1,
          limit: 1,
          filter__is_viewed: 0,
        });

        unviewedNotificationsCount.value = meta.total;
      } catch (err) {
        showErrorBadge(err);
      }
    }

    async function resetUnviewedNotificationsCount() {
      const token = getAccessToken();
      if (!token) return;

      const subId = getSubId(token);
      if (!subId) return;

      try {
        await resetNotifications(subId);
        unviewedNotificationsCount.value = 0;
      } catch (err) {
        console.error('store.pusher.ts', err);
      }
    }

    // formal resetStore
    function $reset() {
      notifications.value = [];
      notificationsRequested.value = false;
      unviewedNotificationsCount.value = 0;
      leavePrivateChannel();
    }

    // private methods
    function showErrorBadge(error: any) {
      const message = error?.msg || error?.message;
      showBadge({ content: message, isError: true });
    }

    return {
      notifications,
      notificationsRequested,
      unviewedNotificationsCount,
      emptyNotifications,
      addNotification,
      updateNotifications,
      setNotificationsRequested,
      setReadToNotification,
      fetchNotifications,
      fetchUnviewedNotificationsCount,
      resetUnviewedNotificationsCount,
      $reset,
    };
  }),
);
